import { z } from 'zod';
import { responseScheme } from '@/models/response';
import { avatarUrlSchema, nameSchema, stringifiedDateSchema } from '@/utils/schemas';

export enum Role {
  Member = 0,
  Admin = 9,
  Owner = 10,
}

export const groupMemberScheme = z.object({
  ID: z.number(),
  GroupID: z.number(),
  GroupName: nameSchema,
  GroupImg: avatarUrlSchema,
  UserID: z.number(),
  UserNickName: nameSchema,
  RemarkName: nameSchema,
  Role: z.number(), // "10" super admin; "9" admin
  GroupMemberName: nameSchema,
  UserHeadImg: avatarUrlSchema,
  Gender: z.number(),
  JoinTime: stringifiedDateSchema,
  ReadStatus: z.number(),
  Status: z.number(),
  OnLine: z.number(),
  OnlineDesc: z.string().nullish(),
});

export const groupScheme = z.object({
  ID: z.number(),
  CreatorID: z.number(),
  CreatorName: nameSchema,
  CreatorHeadImg: avatarUrlSchema,
  CreateTime: stringifiedDateSchema,
  GroupName: nameSchema,
  GroupImg: avatarUrlSchema,
  GroupCode: z.number(),
  GroupType: z.literal(0).or(z.literal(1)).optional().default(0),
  Info: z.string().nullish(),
  UserCount: z.number(),
  MaxUserCount: z.number(),
  JoinType: z.number(),
  ReadDelete: z.number(),
  AutoDeleteTime: z.number(),
  LastMsgTimeStamp: z.number(),
  LastMsg: z.string(),
  AddFriend: z.number().default(0).pipe(z.coerce.boolean()),
  AllBan: z.number().default(0).pipe(z.coerce.boolean()),
  Order: z.number().optional().default(0),
});

export const groupInfoScheme = responseScheme.merge(
  z.object({
    GroupJoinApplyCount: z.number().nullish(),
    GroupData: groupScheme.nullish(),
    Members: z.array(groupMemberScheme).nullish(),
    Self: groupMemberScheme
      .extend({
        GroupName: nameSchema,
        GroupImg: avatarUrlSchema,
        UserHeadImg: avatarUrlSchema,
      })
      .nullish(),
  }),
);

export enum GroupJoinMethod {
  NotAvailable = 0,
  ByQrCode = 1,
  ByInvitation = 2,
  ByLiveFansGroup = 3,
  ByOneStation = 4,
}

export const groupJoinMethodSchema = z.nativeEnum(GroupJoinMethod);

export const groupJoinApplicationScheme = z.object({
  ID: z.number(),
  UserID: z.number(),
  UserNickName: nameSchema,
  UserHeadImg: avatarUrlSchema,
  Source: z.string().nullish(),
  JoinMethod: groupJoinMethodSchema,
  Status: z.union([z.literal(0), z.literal(1), z.literal(2)]).default(0),
  CreateTime: stringifiedDateSchema,
});

export type GroupMember = z.infer<typeof groupMemberScheme>;

export type GroupInfo = z.infer<typeof groupInfoScheme>;

export type Group = z.infer<typeof groupScheme>;

export type GroupJoinApplication = z.infer<typeof groupJoinApplicationScheme>;
